import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setEntryDate, setFeature } from "src/redux/slices/generatorSlice";
import { convertDDMMtoMMDD } from "src/utils/timeUtils/convertDateFormat";
import {
  mantineDateStringToDate,
  toMantineDateBound,
  toMantineDateString,
} from "src/utils/mantineDates";

import { useInputProps } from "../hooks/useInputProps";
import { rem } from "@mantine/core";
import { useTriggerAutoSave } from "../hooks/useTriggerAutoSave";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
import useAmbNewVoyage from "../hooks/useAmbNewVoyage";
import { useCopyVal } from "../hooks/useCopyVal";

dayjs.extend(weekOfYear);

interface IDataPicker {
  id: string;
  externalValue: string;
  required: boolean;
  cellId?: string;
  now?: boolean;
  isFirstRow?: boolean;
  forwardDate?: boolean;
  futureDate?: boolean;
  showWeekday?: boolean;
  tag?: string;
  class?: string;
  rowIndex?: number;
  feature?: string;
  isShared?: boolean;
  copyValTarget?: string;
}

const DatePicker = ({
  id,
  externalValue,
  required,
  cellId,
  now,
  isFirstRow,
  forwardDate,
  futureDate,
  showWeekday,
  rowIndex,
  feature,
  tag,
  class: category,
  isShared,
  copyValTarget,
}: IDataPicker) => {
  const { entrydate } = useSelector(
    state => state.generatorSlice.entryDataToSave,
  );
  const dispatch = useDispatch();
  const triggerAutoSave = useTriggerAutoSave();
  // For shared sections with entry-date, use the main entry date value and disable the picker
  const shouldUseMainEntryDate = isShared && cellId === "entry-date";
  const displayValue = shouldUseMainEntryDate
    ? entrydate
      ? convertDDMMtoMMDD(entrydate)
      : null
    : (convertDDMMtoMMDD(externalValue) ??
      (cellId === "entry-date" && now ? new Date() : null));

  const { onChange, ...inputProps } = useInputProps(id, displayValue, required);
  const inputRef = useRef<HTMLButtonElement | null>(null);

  const { getValues, setValue } = useFormContext();
  const { applyCopyVal } = useCopyVal();
  const { onVoyageChange, modalElement } = useAmbNewVoyage({
    tag,
    getValues,
    setValue,
    rowIndex,
  });

  // Get logbook refs context props for navigation
  const logbookRefsProps = useLogbookRefsContext(id);

  const maxDate = useMemo(
    () =>
      futureDate
        ? undefined
        : forwardDate
          ? new Date(new Date().getTime() + 86400000)
          : new Date(),
    [forwardDate, futureDate],
  );

  const mantineValue = useMemo(
    () => toMantineDateString(inputProps.value ?? displayValue),
    [inputProps.value, displayValue],
  );

  useEffect(() => {
    if (cellId !== "entry-date" || !isFirstRow || !mantineValue || isShared)
      return;

    const selectedDate = dayjs(mantineValue);
    if (selectedDate.format("DD MMM YYYY") === entrydate) return;

    dispatch(setEntryDate(selectedDate.format("DD MMM YYYY")));
  }, [isFirstRow, mantineValue, dispatch, entrydate, cellId, isShared]);

  // Update shared entry-date fields when main entry date changes
  useEffect(() => {
    if (shouldUseMainEntryDate && entrydate) {
      const mainEntryDateValue = convertDDMMtoMMDD(entrydate);
      if (
        mainEntryDateValue &&
        toMantineDateString(mainEntryDateValue) !== mantineValue
      ) {
        onChange(mainEntryDateValue);
      }
    }
  }, [entrydate, shouldUseMainEntryDate, onChange, mantineValue]);

  const handleChange = (v: string | null) => {
    // Prevent any changes for shared entry-date fields (they should be disabled anyway)
    if (shouldUseMainEntryDate) return;

    const dateValue = mantineDateStringToDate(v);
    onChange(dateValue);
    if (isShared) return; // Prevent state updates for other shared fields

    if (feature === "autoday") {
      dispatch(
        setFeature({
          key: "autoday",
          feature: v ? dayjs(v).format("dddd") : "",
        }),
      );
    }
    if (cellId === "entry-date" && v)
      dispatch(setFeature({ key: "autoweek", feature: dayjs(v).week() }));

    applyCopyVal({
      feature,
      targetField: copyValTarget,
      value: dateValue,
    });

    onVoyageChange(dateValue);
    triggerAutoSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    logbookRefsProps.onKeyDown(e);
  };
  return (
    <>
      {modalElement}
      <DatePickerInput
        miw={rem(150)}
        className={`${category ?? ""} rowIndex-${rowIndex ?? ""} ${tag ?? ""}`}
        data-id={id}
        data-unit={"date"}
        key={id}
        id={id}
        mx="auto"
        maxDate={toMantineDateBound(maxDate)}
        valueFormat={`DD MMM YYYY ${showWeekday ? "dddd" : ""}`}
        dropdownType="popover"
        popoverProps={{
          position: "top-start",
          withinPortal: true,
          zIndex: 1000,
        }}
        {...inputProps}
        value={mantineValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        ref={el => {
          inputRef.current = el;
          logbookRefsProps.ref(el);
        }}
        clearable={!shouldUseMainEntryDate}
        disabled={shouldUseMainEntryDate}
      />
    </>
  );
};

export default DatePicker;
